import type { HoldingReport } from '@interfaces/holdingReport';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import type { SubAccountReport } from '@interfaces/subAccountReport';

const holdingHasTargetFields = (holding: HoldingReport): boolean =>
  (holding.expectedValueCents ?? 0) > 0 ||
  (holding.expectedPercentage ?? 0) > 0;

const subAccountHasHoldings = (
  subAccountReport: SubAccountReport | undefined,
): boolean => (subAccountReport?.holdings?.length ?? 0) > 0;

const subAccountHoldingsHaveTargets = (
  subAccountReport: SubAccountReport | undefined,
): boolean =>
  (subAccountReport?.holdings ?? []).some((holding) =>
    holdingHasTargetFields(holding),
  );

const mergeHoldings = (
  existing: HoldingReport[] | undefined,
  optimized: HoldingReport[] | undefined,
): HoldingReport[] | undefined => {
  if (!optimized?.length) {
    return existing;
  }

  if (!existing?.length) {
    return optimized;
  }

  const optimizedByProductId = new Map(
    optimized
      .map((holding) => [holding.financialProduct?.id, holding] as const)
      .filter(([productId]) => Boolean(productId)),
  );

  const mergedExisting = existing.map((existingHolding) => {
    const productId = existingHolding.financialProduct?.id;
    const optimizedHolding = productId
      ? optimizedByProductId.get(productId)
      : undefined;

    if (!optimizedHolding) {
      return existingHolding;
    }

    optimizedByProductId.delete(productId);

    return {
      ...existingHolding,
      ...optimizedHolding,
      expectedValueCents:
        (optimizedHolding.expectedValueCents ?? 0) > 0
          ? optimizedHolding.expectedValueCents
          : existingHolding.expectedValueCents,
      expectedPercentage:
        (optimizedHolding.expectedPercentage ?? 0) > 0
          ? optimizedHolding.expectedPercentage
          : existingHolding.expectedPercentage,
    };
  });

  return [...mergedExisting, ...optimizedByProductId.values()];
};

const mergeSubAccounts = (
  existing: SubAccountReport[] | undefined,
  optimized: SubAccountReport[] | undefined,
): SubAccountReport[] | undefined => {
  if (!optimized?.length) {
    return existing;
  }

  if (!existing?.length) {
    return optimized;
  }

  const optimizedBySubAccountId = new Map(
    optimized
      .map(
        (subAccountReport) =>
          [subAccountReport.subAccount?.id, subAccountReport] as const,
      )
      .filter(([subAccountId]) => Boolean(subAccountId)),
  );

  const mergedExisting = existing.map((existingSubAccount) => {
    const subAccountId = existingSubAccount.subAccount?.id;
    const optimizedSubAccount = subAccountId
      ? optimizedBySubAccountId.get(subAccountId)
      : undefined;

    if (!optimizedSubAccount) {
      return existingSubAccount;
    }

    optimizedBySubAccountId.delete(subAccountId);

    const useOptimizedHoldings =
      subAccountHasHoldings(optimizedSubAccount) &&
      (subAccountHoldingsHaveTargets(optimizedSubAccount) ||
        !subAccountHasHoldings(existingSubAccount));

    return {
      ...existingSubAccount,
      ...optimizedSubAccount,
      holdings: useOptimizedHoldings
        ? optimizedSubAccount.holdings
        : existingSubAccount.holdings,
    };
  });

  return [...mergedExisting, ...optimizedBySubAccountId.values()];
};

const mergePortfolioReport = (
  existing: PortfolioReport,
  optimized: PortfolioReport,
): PortfolioReport => ({
  ...existing,
  ...optimized,
  modelPortfolio: optimized.modelPortfolio ?? existing.modelPortfolio,
  object: optimized.object ?? existing.object,
  subAccounts:
    mergeSubAccounts(existing.subAccounts, optimized.subAccounts) ?? [],
  holdings: mergeHoldings(existing.holdings, optimized.holdings) ?? [],
});

/** Merge optimizer-run tagged reports with STR-backed optimized reports. */
export const mergeRunPortfolioReports = (
  optimizerRunReports: PortfolioReport[],
  optimizedReports: PortfolioReport[],
): PortfolioReport[] => {
  const merged = new Map<string, PortfolioReport>();

  for (const report of optimizerRunReports) {
    merged.set(report.id, report);
  }

  for (const report of optimizedReports) {
    const existing = merged.get(report.id);

    merged.set(
      report.id,
      existing ? mergePortfolioReport(existing, report) : report,
    );
  }

  return [...merged.values()];
};
